import {
  SendOutlined,
  CheckCircleOutlined,
  PeopleOutline,
  AccessTime,
  MonitorHeartOutlined,
  BusinessOutlined,
  AttachMoneyOutlined,
  AccountBalanceWalletOutlined,
} from '@mui/icons-material';
import { Button, CardContainer } from 'finara-components';
import { useNavigate } from 'react-router-dom';

import { useAccounts } from '@/hooks/queries/useAccounts';
import { formatNumber } from '@/utils/formatters';

interface DashboardHeaderProps {
  companyName?: string;
  title?: string;
  subtitle?: string;
  showRealizarPago?: boolean;
  activeEmployees?: number;
  averageProcessingTime?: number;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  companyName = 'TechCorp SA de CV',
  title = 'Panel Administrativo Finara',
  subtitle = 'Plataforma integral para gestionar pagos empresariales, operaciones de nómina y administración de empleados con la máxima seguridad y eficiencia.',
  showRealizarPago = true,
  activeEmployees = 247,
  averageProcessingTime = 0,
}) => {
  const navigate = useNavigate();

  // Fetch organization accounts
  const { data: accountsData, isLoading: isLoadingAccounts } = useAccounts({
    type: 'organization',
    status: 'active',
    limit: 1,
  });

  // Get the main organization account (first one)
  const mainAccount = accountsData?.data[0];

  const handleRealizarPago = () => {
    void navigate('/pagos');
  };

  // Format balance
  const formatBalance = (value: number) => {
    return `$${formatNumber(value)} MXN`;
  };

  // Format processing time
  const formatProcessingTime = (seconds: number) => {
    if (seconds === 0) return null;
    if (seconds < 60) {
      return `${seconds.toFixed(1)}s`;
    }
    return `${(seconds / 60).toFixed(1)}m`;
  };

  const processingTimeFormatted = formatProcessingTime(averageProcessingTime);
  return (
    <CardContainer className="mt-[21px] !pt-7 !px-7 !pb-5 bg-gradient-to-br from-[oklch(.208_.042_265.755)] via-[oklch(.279_.041_260.031)] to-[oklch(.208_.042_265.755)] text-white mb-8">
      <div className="lg:p-4 grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-8 items-center">
        {/* Left side - Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Header info */}
          <div className="flex items-center gap-3 mb-[10.5px]">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <BusinessOutlined />
            </div>
            <div>
              <h1 className="text-[21px] font-bold">{title}</h1>
              <p className="text-slate-300 text-[12.25px]">{companyName}</p>
            </div>
          </div>

          <p className="text-[15.75px] text-slate-300 mb-[21px] leading-relaxed">{subtitle}</p>

          {/* Feature cards grid */}
          <div className="grid grid-cols-2 gap-4 lg:mb-8">
            <div className="sm:flex items-center gap-3 p-3 bg-white/5 rounded-lg backdrop-blur-sm border border-white/10">
              <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
                <CheckCircleOutlined className="text-green-400" fontSize="small" />
              </div>
              <div>
                <div className="text-sm font-medium">Pagos Seguros</div>
                <div className="text-[12.25px] text-slate-400">SPEI y transferencias</div>
              </div>
            </div>

            <div className="sm:flex items-center gap-3 p-3 bg-white/5 rounded-lg backdrop-blur-sm border border-white/10">
              <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <PeopleOutline className="text-blue-400" fontSize="small" />
              </div>
              <div>
                <div className="text-sm font-medium">Gestión Masiva</div>
                <div className="text-[12.25px] text-slate-400">
                  {activeEmployees} empleados activos
                </div>
              </div>
            </div>

            <div className="sm:flex items-center gap-3 p-3 bg-white/5 rounded-lg backdrop-blur-sm border border-white/10">
              <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <AccessTime className="text-purple-400" fontSize="small" />
              </div>
              <div>
                <div className="text-sm font-medium">Tiempo Real</div>
                <div className="text-[12.25px] text-slate-400">Procesamiento inmediato</div>
              </div>
            </div>

            <div className="sm:flex items-center gap-3 p-3 bg-white/5 rounded-lg backdrop-blur-sm border border-white/10">
              <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center">
                <MonitorHeartOutlined className="text-orange-400" fontSize="small" />
              </div>
              <div>
                <div className="text-sm font-medium">Monitoreo 24/7</div>
                <div className="text-[12.25px] text-slate-400">Alertas y notificaciones</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Payment center card */}
        <div>
          <div className="bg-white/5 rounded-2xl backdrop-blur-sm border border-white/10 p-6 mb-[21px]">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto shadow-xl mb-[14px]">
                <AttachMoneyOutlined fontSize="large" />
              </div>
              <h3 className="text-[17.5px] font-semibold mb-2">Centro de Pagos</h3>
              <p className="text-[12.25px] text-slate-300 mb-[14px]">
                Procesa pagos individuales o masivos con autorización segura
              </p>

              {showRealizarPago && (
                <Button
                  variant="white"
                  size="small"
                  startIcon={<SendOutlined fontSize="inherit" />}
                  className="!p-[12px] !text-sm w-full mb-[14px]"
                  onClick={handleRealizarPago}
                >
                  Realizar Pago
                </Button>
              )}
            </div>
          </div>
          <div className="flex justify-center gap-3 text-center">
            <div className="bg-white/5 rounded-2xl backdrop-blur-sm border border-white/10 p-[10.5px] flex-1">
              {isLoadingAccounts ? (
                <>
                  <div className="text-[21px] font-bold">...</div>
                  <div className="text-[10.5px] text-slate-400">Cargando...</div>
                </>
              ) : mainAccount ? (
                <>
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <AccountBalanceWalletOutlined fontSize="small" className="text-white" />
                  </div>
                  <div className="text-[18px] font-bold">{formatBalance(mainAccount.balance)}</div>
                  <div className="text-[14px] text-slate-400">Cuenta Principal</div>
                  <div className="text-[16px] text-white mt-1 font-mono truncate">
                    Clabe: {mainAccount.clabe}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-[21px] font-bold">N/A</div>
                  <div className="text-[10.5px] text-slate-400">Sin cuenta</div>
                </>
              )}
            </div>
            {processingTimeFormatted && (
              <div className="bg-white/5 rounded-2xl backdrop-blur-sm border border-white/10 p-[10.5px]">
                <div className="text-[21px] font-bold">{processingTimeFormatted}</div>
                <div className="text-[10.5px] text-slate-400">Tiempo promedio</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </CardContainer>
  );
};
